import { Routes, Route } from 'react-router-dom';
import { MainLayout } from '../layouts/MainLayout';
import { HomePage } from '../pages/HomePage';
import { ServicesPage } from '../pages/ServicesPage';
import { AboutPage } from '../pages/AboutPage';
import { HowItWorksPage } from '../pages/HowItWorksPage';
import { BookingPage } from '../pages/BookingPage';
import { AdminLoginPage } from '../pages/AdminLoginPage';
import { AdminDashboardPage } from '../pages/AdminDashboardPage';
import { ProtectedRoute } from '../components/ProtectedRoute';

// Service Page Components
import { SchoolFamilySupportPage } from '../components/SchoolFamilySupportPage';
import { LegalSupportPage } from '../components/LegalSupportPage';
import { RealEstatePage } from '../components/RealEstatePage';
import { HomeManagementPage } from '../components/HomeManagementPage';
import { SpiritualWellnessPage } from '../components/SpiritualWellnessPage';
import { BusinessConsultingPage } from '../components/BusinessConsultingPage';
import { BeverageDevelopmentPage } from '../components/BeverageDevelopmentPage';
import { EliteConciergePage } from '../components/EliteConciergePage';

// Pathway Components
import { ForFamiliesPathway } from '../components/ForFamiliesPathway';
import { ForEntrepreneursPathway } from '../components/ForEntrepreneursPathway';
import { ForHighNetWorthPathway } from '../components/ForHighNetWorthPathway';

import { useNavigate } from 'react-router-dom';

// Higher-order component to add navigation props to service pages
function withNavigation<T extends object>(
  Component: React.ComponentType<T & { onNavigateHome: () => void; onNavigateToHowItWorks: () => void; }>
) {
  return function WrappedComponent(props: T) {
    const navigate = useNavigate();
    
    const handleNavigateHome = () => {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToHowItWorks = () => {
      navigate('/how-it-works');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <Component 
        {...props} 
        onNavigateHome={handleNavigateHome}
        onNavigateToHowItWorks={handleNavigateToHowItWorks}
      />
    );
  };
}

// Higher-order component for components that need booking navigation
function withBookingNavigation<T extends object>(
  Component: React.ComponentType<T & { 
    onNavigateHome: () => void; 
    onNavigateToHowItWorks: () => void; 
    onNavigateToFreeConsultation: () => void;
    onNavigateToPaidSession: () => void;
  }>,
  serviceId: string
) {
  return function WrappedComponent(props: T) {
    const navigate = useNavigate();
    
    const handleNavigateHome = () => {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToHowItWorks = () => {
      navigate('/how-it-works');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToFreeConsultation = () => {
      navigate('/booking', { 
        state: { 
          preSelectedService: serviceId,
          isPaidSession: false 
        } 
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToPaidSession = () => {
      navigate('/booking', { 
        state: { 
          preSelectedService: serviceId,
          isPaidSession: true 
        } 
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <Component 
        {...props} 
        onNavigateHome={handleNavigateHome}
        onNavigateToHowItWorks={handleNavigateToHowItWorks}
        onNavigateToFreeConsultation={handleNavigateToFreeConsultation}
        onNavigateToPaidSession={handleNavigateToPaidSession}
      />
    );
  };
}

// Higher-order component for ForFamiliesPathway
function withFamiliesNavigation(
  Component: React.ComponentType<{
    onNavigateHome: () => void;
    onNavigateToSchoolSupport: () => void;
    onNavigateToRealEstate: () => void;
    onNavigateToHomeManagement: () => void;
  }>
) {
  return function WrappedComponent() {
    const navigate = useNavigate();
    
    const handleNavigateHome = () => {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToService = (servicePath: string) => {
      navigate(servicePath);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <Component 
        onNavigateHome={handleNavigateHome}
        onNavigateToSchoolSupport={() => handleNavigateToService('/services/school-family-support')}
        onNavigateToRealEstate={() => handleNavigateToService('/services/real-estate')}
        onNavigateToHomeManagement={() => handleNavigateToService('/services/home-management')}
      />
    );
  };
}

// Higher-order component for ForEntrepreneursPathway
function withEntrepreneursNavigation(
  Component: React.ComponentType<{
    onNavigateHome: () => void;
    onNavigateToBusinessConsulting: () => void;
    onNavigateToLegalSupport: () => void;
    onNavigateToBeverageDevelopment: () => void;
  }>
) {
  return function WrappedComponent() {
    const navigate = useNavigate();
    
    const handleNavigateHome = () => {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToService = (servicePath: string) => {
      navigate(servicePath);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <Component 
        onNavigateHome={handleNavigateHome}
        onNavigateToBusinessConsulting={() => handleNavigateToService('/services/business-consulting')}
        onNavigateToLegalSupport={() => handleNavigateToService('/services/legal-support')}
        onNavigateToBeverageDevelopment={() => handleNavigateToService('/services/beverage-development')}
      />
    );
  };
}

// Higher-order component for ForHighNetWorthPathway
function withHighNetWorthNavigation(
  Component: React.ComponentType<{
    onNavigateHome: () => void;
    onNavigateToEliteConcierge: () => void;
    onNavigateToSpiritualWellness: () => void;
    onNavigateToHomeManagement: () => void;
  }>
) {
  return function WrappedComponent() {
    const navigate = useNavigate();
    
    const handleNavigateHome = () => {
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigateToService = (servicePath: string) => {
      navigate(servicePath);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <Component 
        onNavigateHome={handleNavigateHome}
        onNavigateToEliteConcierge={() => handleNavigateToService('/services/elite-concierge')}
        onNavigateToSpiritualWellness={() => handleNavigateToService('/services/spiritual-wellness')}
        onNavigateToHomeManagement={() => handleNavigateToService('/services/home-management')}
      />
    );
  };
}

// Wrapped components
const SchoolFamilySupportPageWithNav = withBookingNavigation(SchoolFamilySupportPage, 'school');
const LegalSupportPageWithNav = withBookingNavigation(LegalSupportPage, 'legal');
const RealEstatePageWithNav = withBookingNavigation(RealEstatePage, 'realestate');
const HomeManagementPageWithNav = withBookingNavigation(HomeManagementPage, 'homemanagement');
const SpiritualWellnessPageWithNav = withBookingNavigation(SpiritualWellnessPage, 'spiritual');
const BusinessConsultingPageWithNav = withBookingNavigation(BusinessConsultingPage, 'business');
const BeverageDevelopmentPageWithNav = withBookingNavigation(BeverageDevelopmentPage, 'beverage');
const EliteConciergePageWithNav = withBookingNavigation(EliteConciergePage, 'concierge');

const ForFamiliesPathwayWithNav = withFamiliesNavigation(ForFamiliesPathway);
const ForEntrepreneursPathwayWithNav = withEntrepreneursNavigation(ForEntrepreneursPathway);
const ForHighNetWorthPathwayWithNav = withHighNetWorthNavigation(ForHighNetWorthPathway);

export function AppRouter() {
  return (
    <Routes>
      {/* Admin Routes - Outside MainLayout */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        } 
      />

      {/* Main Application Routes */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<HomePage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="how-it-works" element={<HowItWorksPage />} />
        <Route path="booking" element={<BookingPage />} />
        
        {/* Individual Service Pages */}
        <Route path="services/school-family-support" element={<SchoolFamilySupportPageWithNav />} />
        <Route path="services/legal-support" element={<LegalSupportPageWithNav />} />
        <Route path="services/real-estate" element={<RealEstatePageWithNav />} />
        <Route path="services/home-management" element={<HomeManagementPageWithNav />} />
        <Route path="services/spiritual-wellness" element={<SpiritualWellnessPageWithNav />} />
        <Route path="services/business-consulting" element={<BusinessConsultingPageWithNav />} />
        <Route path="services/beverage-development" element={<BeverageDevelopmentPageWithNav />} />
        <Route path="services/elite-concierge" element={<EliteConciergePageWithNav />} />
        
        {/* Pathway Pages */}
        <Route path="pathways/families" element={<ForFamiliesPathwayWithNav />} />
        <Route path="pathways/entrepreneurs" element={<ForEntrepreneursPathwayWithNav />} />
        <Route path="pathways/high-net-worth" element={<ForHighNetWorthPathwayWithNav />} />
      </Route>
    </Routes>
  );
}
